import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { AgreementPageLayerProps } from 'components/AgreementPdfViewer';
import useMatchMedia from 'hooks/useMatchMedia';
import { getPointInPoints, pointsToPixels } from 'agreementCoordinatesUtils';
import FieldEditorContext from '../../providers/FieldEditor/FieldEditor.context';
import FieldBox from '../FieldBox';
import FieldSettings from '../FieldSettings';
import {
  FIELD_TYPE_DRAG_FORMAT,
  getFieldSettingsPosition,
  getFieldTypeConfig,
  getIsFieldTypeDrag,
} from '../../utils';

import './PageFieldsLayer.styles.scss';

const BASE_FONT_SIZE = 11;

const PageFieldsLayer: React.FC<AgreementPageLayerProps> = (props) => {
  const { pageNumber, scale, pageSize } = props;

  const {
    fields,
    selectedField,
    placingConfig,
    isDraggingField,
    addField,
    setSelectedFieldId,
  } = useContext(FieldEditorContext);

  const isPhablet = useMatchMedia('isPhablet');

  const pageFields = useMemo(
    () => fields.filter(({ page }) => page === pageNumber),
    [fields, pageNumber],
  );

  const classes = classNames('page-fields-layer', {
    'page-fields-layer--placing': !!placingConfig,
  });

  const getEventPoint = (event: React.MouseEvent<HTMLDivElement>) =>
    getPointInPoints(
      event.clientX,
      event.clientY,
      event.currentTarget.getBoundingClientRect(),
      scale,
    );

  const onClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!placingConfig) {
      setSelectedFieldId(undefined);
      return;
    }

    addField(pageNumber, getEventPoint(event), pageSize);
  };

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!getIsFieldTypeDrag(event.dataTransfer)) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (!getIsFieldTypeDrag(event.dataTransfer)) return;

    event.preventDefault();

    addField(
      pageNumber,
      getEventPoint(event),
      pageSize,
      getFieldTypeConfig(event.dataTransfer.getData(FIELD_TYPE_DRAG_FORMAT)),
    );
  };

  return (
    <div
      className={classes}
      style={
        {
          '--field-box-font-size': `${BASE_FONT_SIZE * scale}px`,
        } as React.CSSProperties
      }
      onClick={onClick}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {pageFields.map((field) => (
        <FieldBox
          key={field.id}
          field={field}
          scale={scale}
          pageSize={pageSize}
        />
      ))}

      {!isPhablet && !isDraggingField && selectedField?.page === pageNumber && (
        <FieldSettings
          key={selectedField.id}
          field={selectedField}
          style={getFieldSettingsPosition(
            pointsToPixels(selectedField, scale),
            pointsToPixels({ x: 0, y: 0, ...pageSize }, scale),
          )}
        />
      )}
    </div>
  );
};

export default PageFieldsLayer;
